/**
 * Dahua XVR Penta-brid DVR catalog — hybrid HDCVI/AHD/TVI/CVBS/IP recorders.
 *
 * Every entry below is verified against a primary Dahua datasheet PDF
 * fetched on 2026-05-14 from dahuasecurity.com / material.dahuasecurity.com.
 * The exact source URL appears as a comment above each entry. Fields are
 * left undefined when the datasheet does not list a value — never guessed.
 *
 * All XVR units are tagged `category: 'nvr-recorder'` with `hybrid: true`.
 * `channels` = analog BNC inputs; IP overlay shown in tagline.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_XVR: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────────────
  // COOPER SERIES — legacy non-I (no WizSense AI chip)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr1b04',
    manufacturer: 'Dahua', model: 'XVR1B04',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 1080N/720p Cooper 1U — 4 analog + 5 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1B04-08_datasheet_20201027.pdf',
    specs: {
      channels: 4, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b08',
    manufacturer: 'Dahua', model: 'XVR1B08',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 1080N/720p Cooper 1U — 8 analog + 10 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR1B04-08_datasheet_20201027.pdf',
    specs: {
      channels: 8, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b04h',
    manufacturer: 'Dahua', model: 'XVR1B04H',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4M-N/1080p Cooper 1U — 5MP HDCVI / 6MP IP, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20181227/XVR1B04-08H_Datasheet_20181227.pdf',
    specs: {
      channels: 4, maxResolution: '4M-N (5 MP HDCVI)',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b08h',
    manufacturer: 'Dahua', model: 'XVR1B08H',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4M-N/1080p Cooper 1U — 5MP HDCVI / 6MP IP, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20181227/XVR1B04-08H_Datasheet_20181227.pdf',
    specs: {
      channels: 8, maxResolution: '4M-N (5 MP HDCVI)',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // COOPER-I SERIES — WizSense (onboard AI for SMD Plus / Perimeter)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr1b04-i',
    manufacturer: 'Dahua', model: 'XVR1B04-I',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 1080N/720p Cooper WizSense 1U — 5 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B04-I_V2_datasheet_20230302.pdf',
    specs: {
      channels: 4, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b08-i',
    manufacturer: 'Dahua', model: 'XVR1B08-I',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 1080N/720p Cooper WizSense 1U — 10 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B08-I_V2_datasheet_20240229.pdf',
    specs: {
      channels: 8, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b16-i',
    manufacturer: 'Dahua', model: 'XVR1B16-I',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 1080N/720p Cooper WizSense 1U — 18 IP overlay, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B16-I_datasheet_20220530.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 64, outputBandwidthMbps: 64,
      maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b04h-i',
    manufacturer: 'Dahua', model: 'XVR1B04H-I',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 5M-N/1080p Cooper WizSense 1U — 6 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B04H-I_datasheet_20220530.pdf',
    specs: {
      channels: 4, maxResolution: '5M-N (5 MP HDCVI)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b08h-i',
    manufacturer: 'Dahua', model: 'XVR1B08H-I',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 5M-N/1080p Cooper WizSense 1U — 12 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B08H-I_datasheet_20220620.pdf',
    specs: {
      channels: 8, inputBandwidthMbps: 64,
      maxResolution: '5M-N (5 MP HDCVI)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr1b16h-i',
    manufacturer: 'Dahua', model: 'XVR1B16H-I',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 5MP Value/1080p Cooper WizSense 1U — 24 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR1B16H-I_datasheet_20230720.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4b04-i',
    manufacturer: 'Dahua', model: 'XVR4B04-I',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 1080N/720p Cooper WizSense 1U — 5 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/soft/20220525/DH-XVR4B04-I_datasheet_20220526.pdf',
    specs: {
      channels: 4, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4b08-i',
    manufacturer: 'Dahua', model: 'XVR4B08-I',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 1080N/720p Cooper WizSense 1U — 10 IP overlay, 1 SATA (6 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/soft/20220531/DH-XVR4B08-I_datasheet_20220531.pdf',
    specs: {
      channels: 8, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 6, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // LITE 4000 SERIES — WizSense AI by Recorder (no 4K)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr4104hs-i',
    manufacturer: 'Dahua', model: 'XVR4104HS-I',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 1080N/720p Lite WizSense Compact 1U — 5 IP overlay, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4104HS-I_datasheet_20220905.pdf',
    specs: {
      channels: 4, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4108hs-i',
    manufacturer: 'Dahua', model: 'XVR4108HS-I',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 1080N/720p Lite WizSense Compact 1U — 10 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4108HS-I_datasheet_20220530.pdf',
    specs: {
      channels: 8, maxResolution: '1080N/720p',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4116hs-i',
    manufacturer: 'Dahua', model: 'XVR4116HS-I',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 720p Lite WizSense Compact 1U — 18 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4116HS-I_datasheet_20220530.pdf',
    specs: {
      channels: 16, maxResolution: '720p (1080N)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4216an-i',
    manufacturer: 'Dahua', model: 'XVR4216AN-I',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 720p Lite WizSense 1U — 18 IP overlay (6 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4216AN-I_datasheet_20220413.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 64,
      maxResolution: '720p (1080N)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr4232an-i',
    manufacturer: 'Dahua', model: 'XVR4232AN-I',
    category: 'nvr-recorder',
    tagline: '32-ch Penta-brid 1080N/720p Lite WizSense 1U — IP overlay (6 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR4232AN-I_datasheet_20220622.pdf',
    specs: {
      channels: 32, maxResolution: '1080N/720p',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // LITE 5000 SERIES — WizSense (5 MP analog, 6 MP IP, AI by recorder)
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr5104hs-i3',
    manufacturer: 'Dahua', model: 'XVR5104HS-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 5MP Value/1080p WizSense Compact 1U — 6 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104HS-I3_V3_datasheet_20230719.pdf',
    specs: {
      channels: 4, inputBandwidthMbps: 32,
      maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5104h-i3',
    manufacturer: 'Dahua', model: 'XVR5104H-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 5MP Value/1080p WizSense Mini 1U — 6 IP overlay, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104H-I3_V3_datasheet_20240717.pdf',
    specs: {
      channels: 4, maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5108hs-i3',
    manufacturer: 'Dahua', model: 'XVR5108HS-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 5M-N/1080p WizSense Compact 1U — 12 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5108HS-I3_datasheet_20220530.pdf',
    specs: {
      channels: 8, maxResolution: '5M-N (5 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5108he-i3',
    manufacturer: 'Dahua', model: 'XVR5108HE-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 5MP Value/1080p WizSense Mini 1U — alarm 8/3, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5108HE-I3_V3_datasheet_20240717.pdf',
    specs: {
      channels: 8, maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5116hs-i3',
    manufacturer: 'Dahua', model: 'XVR5116HS-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 5MP Value/1080p WizSense Compact 1U — 24 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5116HS-I3_datasheet_20230720.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5116h-i3',
    manufacturer: 'Dahua', model: 'XVR5116H-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 5MP Value/1080p WizSense Mini 1U — 24 IP overlay (6 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5116H-I3_datasheet_20230720.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5216an-i3',
    manufacturer: 'Dahua', model: 'XVR5216AN-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 5MP Value/1080p WizSense 1U — IP overlay (6 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5216AN-I3_datasheet_20240722.pdf',
    specs: {
      channels: 16, maxResolution: '5 MP',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5232an-i3',
    manufacturer: 'Dahua', model: 'XVR5232AN-I3',
    category: 'nvr-recorder',
    tagline: '32-ch Penta-brid 5M-N/1080p WizSense 1U — IP overlay (6 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5232AN-I3_datasheet_20230113.pdf',
    specs: {
      channels: 32, maxResolution: '5M-N (5 MP)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // LITE 5000 4KL-I3 SERIES — 4K Value WizSense
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr5104hs-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5104HS-4KL-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4K Value/5MP WizSense Compact 1U — 8 IP overlay (8 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104HS-4KL-I3_V3_datasheet_20231027.pdf',
    specs: {
      channels: 4, inputBandwidthMbps: 64,
      maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5104h-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5104H-4KL-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4K Value/5MP WizSense Mini 1U — 8 IP overlay (8 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104H-4KL-I3_V3_datasheet_20230720.pdf',
    specs: {
      channels: 4, inputBandwidthMbps: 64,
      maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5104he-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5104HE-4KL-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4K Value/5MP WizSense Mini 1U — alarm 8/3, 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5104HE-4KL-I3_V3_datasheet_20240717.pdf',
    specs: {
      channels: 4, maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 10/100 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5108hs-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5108HS-4KL-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K Value/5MP WizSense Compact 1U — 16 IP overlay (8 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5108HS-4KL-I3_V2_datasheet_20230720.pdf',
    specs: {
      channels: 8, maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5108h-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5108H-4KL-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K-N/5MP WizSense Mini 1U — 16 IP overlay (8 MP), 1 SATA (16 TB)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5108H-4KL-I3_datasheet_20230107.pdf',
    specs: {
      channels: 8, inputBandwidthMbps: 128,
      maxResolution: '4K-N / 5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5116he-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5116HE-4KL-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K-N/5MP WizSense Mini 1U — alarm 16/3, 1 SATA (16 TB), gigabit',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5116HE-4KL-I3_datasheet_20220908.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '4K-N / 5 MP',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '16 in / 3 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5208an-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5208AN-4KL-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K Value/5MP WizSense 1U — 16 IP overlay (8 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5208AN-4KL-I3_V2_datasheet_20240723.pdf',
    specs: {
      channels: 8, inputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5216a-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5216A-4KL-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K Value/5MP WizSense 1U — alarm 16/3, 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5216A-4KL-I3_V2_datasheet_20230720.pdf',
    specs: {
      channels: 16, maxResolution: '4K (8 MP)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '16 in / 3 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5216an-4kl-i3',
    manufacturer: 'Dahua', model: 'XVR5216AN-4KL-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K-N/5MP WizSense 1U — IP overlay (8 MP), 2 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5216AN-4KL-I3_datasheet_20230107.pdf',
    specs: {
      channels: 16, maxResolution: '4K-N / 5 MP',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr5432l-i3',
    manufacturer: 'Dahua', model: 'XVR5432L-I3',
    category: 'nvr-recorder',
    tagline: '32-ch Penta-brid 5MP Value/1080p WizSense 1.5U — alarm 16/6, 4 SATA (16 TB each)',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR5432L-I3_datasheet_20240723.pdf',
    specs: {
      channels: 32, maxResolution: '5 MP',
      hddBays: 4, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U', alarmIO: '16 in / 6 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },

  // ─────────────────────────────────────────────────────────────────────
  // PRO 7000 SERIES — 4KL-X legacy + 4K-I3 WizSense
  // ─────────────────────────────────────────────────────────────────────
  {
    id: 'dahua-xvr7104he-4kl-x',
    manufacturer: 'Dahua', model: 'XVR7104HE-4KL-X',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4K Pro Mini 1U — alarm 8/3, 1 SATA (10 TB), IoT/POS',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/soft/20201030/DH-XVR7104HE-4KL-X_datasheet_20201027.pdf',
    specs: {
      channels: 4, maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 10, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 100M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7108he-4kl-x',
    manufacturer: 'Dahua', model: 'XVR7108HE-4KL-X',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K Pro Mini 1U — alarm 8/3, 1 SATA (10 TB), gigabit, IoT/POS',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf',
    specs: {
      channels: 8, inputBandwidthMbps: 64,
      maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 10, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 1000M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7116he-4kl-x',
    manufacturer: 'Dahua', model: 'XVR7116HE-4KL-X',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K Pro Mini 1U — alarm 16/3, 1 SATA (10 TB), gigabit, IoT/POS',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7108-16HE-4KL-X_datasheet_20201027.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 10, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '16 in / 3 out',
      networkPorts: '1× 1000M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7208a-4k-x',
    manufacturer: 'Dahua', model: 'XVR7208A-4K-X',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K Pro 1U — alarm 8/3, 2 SATA (10 TB each), gigabit, IoT/POS',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/asset/upload/uploads/soft/20201030/DH-XVR7208A-4K-X_datasheet_20201027.pdf',
    specs: {
      channels: 8, inputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 2, hddBayMaxTb: 10, poeInPorts: 0,
      codecs: ['H.265+', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 1000M RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7104he-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7104HE-4K-I3',
    category: 'nvr-recorder',
    tagline: '4-ch Penta-brid 4K WizSense Mini 1U — alarm 8/3, 1 SATA (16 TB), gigabit',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7104HE-4K-I3_datasheet_20230720.pdf',
    specs: {
      channels: 4, maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7108he-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7108HE-4K-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K WizSense Mini 1U — alarm 8/3, 1 SATA (16 TB), gigabit',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7108HE-4K-I3_datasheet_20230720.pdf',
    specs: {
      channels: 8, maxResolution: '4K (8 MP)',
      hddBays: 1, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7208a-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7208A-4K-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K WizSense 1U — alarm 8/3, 2 SATA (16 TB each), gigabit',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7208A-4K-I3_datasheet_20230720.pdf',
    specs: {
      channels: 8, maxResolution: '4K (8 MP)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', alarmIO: '8 in / 3 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7216an-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7216AN-4K-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K WizSense 1U — IP overlay (8 MP), 2 SATA (16 TB each), gigabit',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7216AN-4K-I3_datasheet_20230720.pdf',
    specs: {
      channels: 16, inputBandwidthMbps: 128,
      maxResolution: '4K (8 MP)',
      hddBays: 2, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '1U', networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7416l-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7416L-4K-I3',
    category: 'nvr-recorder',
    tagline: '16-ch Penta-brid 4K WizSense 1.5U — alarm 16/6, 4 SATA (16 TB each), gigabit',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7416L-4K-I3_datasheet_20240808.pdf',
    specs: {
      channels: 16, maxResolution: '4K (8 MP)',
      hddBays: 4, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U', alarmIO: '16 in / 6 out',
      networkPorts: '1× 10/100/1000 RJ-45', hybrid: true,
    },
  },
  {
    id: 'dahua-xvr7808s-4k-i3',
    manufacturer: 'Dahua', model: 'XVR7808S-4K-I3',
    category: 'nvr-recorder',
    tagline: '8-ch Penta-brid 4K WizSense 2U — alarm 16/6, 8 SATA (16 TB each), 2× gigabit, eSATA',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/XVR7808S-4K-I3_datasheet_20230720.pdf',
    specs: {
      channels: 8, maxResolution: '4K (8 MP)',
      hddBays: 8, hddBayMaxTb: 16, poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264+', 'H.264'],
      onvif: true, formFactor: '2U', alarmIO: '16 in / 6 out (5 relay + 1× 12V/1A)',
      networkPorts: '2× 10/100/1000 RJ-45', hybrid: true,
    },
  },
];
